<template>
  <div>
    <b-row v-if="device.network.networkType.identifier === 'virtual-network'">
      <b-col>
        <b-card no-body>
          <b-card-header class="border-bottom">
            <h4 class="mb-0">
              Mock Sensor Data
            </h4>
            <b-button
              :disabled="!isAnySensorSelected"
              variant="primary"
              @click="sendSensorData"
            >
              Send
            </b-button>
          </b-card-header>

          <b-table
            :items="sensors"
            :fields="mockSensorFields"
          >
            <template #cell(select)="row">
              <b-form-checkbox
                v-model="row.item.selected"
                switch
                @change="!row.item.selected ? (row.item.value = '') : ''"
              />
            </template>

            <template #cell(mockValue)="row">
              <div v-if="row.item.type === 'boolean'">
                <v-select
                  v-model="row.item.value"
                  :options="booleanOptions"
                  :disabled="!row.item.selected"
                />
              </div>
              <div v-else-if="row.item.type === 'double'">
                <b-form-input
                  v-model="row.item.value"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  :disabled="!row.item.selected"
                />
              </div>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        v-for="sensor in sensors"
        :key="sensor.name"
      >
        <b-card
          :title="sensor.name"
          :footer="lastUpdated(sensor.identifier)"
          class="border"
        >
          <h1>
            {{
              findSensorValue(sensor.identifier, sensor.type) +
                " " +
                checkUnit(sensor.unit)
            }}
          </h1>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-card no-body>
          <b-card-header>
            <b-col>
              <div>
                <b-card-title class="mb-1">
                  Historical Sensor Data
                </b-card-title>
                <b-card-sub-title>Line Chart</b-card-sub-title>
              </div>
            </b-col>

            <b-col v-if="sensors.length > 0">
              <v-select
                v-model="selectedSensor"
                :disabled="gettingSensorData"
                placeholder="getPlaceholder"
                :options="sensors"
                :get-option-label="(sensor) => sensor.name"
                @input="getSensorData"
              />
            </b-col>
          </b-card-header>

          <b-card-body v-if="series[0].data.length > 0">
            <vue-apex-charts
              type="area"
              height="400"
              :options="options"
              :series="series"
            />
            {{ series[0].data.length }} records
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCol,
  BCard,
  BCardHeader,
  BCardTitle,
  BCardSubTitle,
  BRow,
  BTable,
  BButton,
  BFormInput,
  BFormCheckbox,
  BCardBody,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import vSelect from 'vue-select'
import { API } from 'aws-amplify'

export default {
  components: {
    BCol,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardSubTitle,
    BRow,
    BTable,
    BButton,
    VueApexCharts,
    vSelect,
    BFormInput,
    BFormCheckbox,
    BCardBody,
  },
  props: {
    device: {
      type: Object,
      required: true,
    },
    sensors: {
      type: Array,
      required: true,
    },
    workspace: {
      type: String,
      required: true,
    },
    workspacetoken: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      booleanOptions: ['true', 'false'],
      sensorData: [],
      mockSensorFields: [
        { key: 'select', label: 'Select' },
        { key: 'name', label: 'Sensor Name', sortable: true },
        { key: 'description', label: 'Description', sortable: true },
        { key: 'dimension', label: 'Dimension', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'unit', label: 'Unit', sortable: true },
        { key: 'mockValue', label: 'Mock Value', sortable: true },
      ],
      gettingSensorData: false,
      selectedSensor: {},
      todaysDate: '',
      series: [{ data: [] }],
      options: {
        chart: {
          id: 'area-datetime',
          type: 'area',
          height: 350,
          zoom: { autoScaleYaxis: true },
        },
        dataLabels: { enabled: false },
        markers: { size: 0, style: 'hollow' },
        xaxis: { type: 'datetime', min: '', tickAmount: 1 },
        yaxis: { title: { text: '' } },
        tooltip: {
          x: { format: 'HH:mm:ss dd MMM yyyy' },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.5,
            opacityTo: 0.9,
            stops: [0, 100],
          },
        },
      },
    }
  },
  computed: {
    isAnySensorSelected() {
      return this.sensors.some(sensor => sensor.selected)
    },
    getPlaceholder() {
      return 'Select Sensor'
    },
    selectedSensorName() {
      return this.selectedSensor.name || ''
    },
    selectedSensorUnit() {
      return this.selectedSensor.unit || ''
    },
  },
  created() {
    this.getLastSensorMeasurements()
  },
  methods: {
    async sendSensorData() {
      const sensorPayload = {
        id: this.device.id,
        timestamp: new Date().toISOString(),
        sensorData: {},
      }

      this.sensors.forEach(sensor => {
        if (sensor.selected) {
          let { value } = sensor

          // Check if value can be converted to a number.
          if (!Number.isNaN(Number(value))) {
            value = Number(value)
          }
          // If it's a boolean value, convert it to a boolean.
          if (value === 'true') {
            value = true
          } else if (value === 'false') {
            value = false
          }
          // if it is a string, convert it to a string.
          if (typeof value === 'string') {
            value = String(value)
          }
          sensorPayload.sensorData[sensor.identifier] = value
        }
      })
      const headers = {
        'Content-Type': 'application/json',
        workspace: this.workspace,
        workspacetoken: this.workspacetoken,
      }
      try {
        await API.post('NetworkAPI', '/uplinks', {
          headers,
          body: sensorPayload,
        })

        this.getLastSensorMeasurements()
        if (this.selectedSensor) {
          this.getSensorData()
        }
      } catch (error) {
        // console.log(error)
      }
    },
    checkUnit(unit) {
      return unit || ''
    },
    findSensorValue(identifier, type) {
      const identified = this.sensorData.find(
        data => data.measure_name === identifier,
      )
      if (identified) {
        return identified[`measure_value::${type}`] || 0
      }
      return 0
    },
    async getLastSensorMeasurements() {
      const promises = this.sensors.map(async sensor => {
        const db = this.workspace
        const table = this.device.deviceModel.id
        const apiName = 'TimestreamAPI'
        const path = '/items'
        const query = `SELECT * FROM "${db}.${process.env.VUE_APP_AMPLIFY_ENV}"."${table}" where id = '${this.device.id}' and measure_name = '${sensor.identifier}'  ORDER BY time DESC LIMIT 1`
        // console.log(query)
        const myInit = {
          headers: {
            workspace: this.workspace,
            workspacetoken: this.workspacetoken,
          },
          queryStringParameters: {
            query,
          },
        }
        const response = await API.get(apiName, path, myInit)
        // console.log(response)
        const lastEntry = response.timestreamData[0][0]
        return lastEntry
      })
      const sensorData = await Promise.all(promises)
      this.sensorData = sensorData.filter(entry => entry !== undefined)
    },

    lastUpdated(identifier) {
      const identified = this.sensorData.find(
        data => data.measure_name === identifier,
      )
      if (identified) {
        const utcTime = `${identified.time} UTC`
        const datetime = new Date(utcTime)
        return `Last Updated: ${datetime.toLocaleString()}`
      }
      return 'Last Updated: No Activity'
    },
    async getSensorData() {
      this.gettingSensorData = true
      this.series[0].data = []

      const { selectedSensorName } = this
      const { selectedSensorUnit } = this

      this.series[0].name = selectedSensorName
      this.options.yaxis.title.text = selectedSensorUnit
        ? `${selectedSensorName} (${selectedSensorUnit})`
        : selectedSensorName

      this.options.tooltip.y = {
        formatter: val => (selectedSensorUnit ? `${val} ${selectedSensorUnit}` : val),
      }

      const workspace = JSON.parse(localStorage.getItem('workspace')).id
      const db = `${workspace}.${process.env.VUE_APP_AMPLIFY_ENV}`
      const table = this.device.deviceModel.id
      const apiName = 'TimestreamAPI'
      const path = '/items'
      const query = `SELECT * FROM "${db}"."${table}" where id = '${this.device.id}' and measure_name = '${this.selectedSensor.identifier}' ORDER BY time DESC LIMIT 2500`

      const myInit = {
        headers: {
          workspace,
          workspacetoken: this.workspacetoken,
        },
        queryStringParameters: {
          query,
        },
      }

      try {
        const response = await API.get(apiName, path, myInit)
        response.timestreamData[0].forEach(record => {
          let measureValue = record[`measure_value::${this.selectedSensor.type}`]
          if (this.selectedSensor.type === 'boolean') {
            // Convert boolean values to numbers
            measureValue = measureValue === true ? 1 : 0
          }
          const dateTime = new Date(`${record.time} UTC`).getTime()
          this.series[0].data.push([dateTime, measureValue])
        })
      } catch (error) {
        // console.log(error)
      } finally {
        this.gettingSensorData = false
      }
    },
  },
}
</script>

<style>
@import "~vue-select/dist/vue-select.css";
</style>
